// Manually authored blog posts. No automation, no generated imagery:
// every image below is an asset that already exists in this project.

import heroRepairVsReplace from "@/assets/blog/Roof_Repair_vs_Replacement.jpg.asset.json";
import leakCauses from "@/assets/blog/What_Causes_Roof_Leaks_in_San_Diego.jpg.asset.json";
import repairBasics from "@/assets/blog/What_Homeowners_Need_to_Know.jpg.asset.json";
import repairCost from "@/assets/blog/howmuchdoesroofrepair_real2026.jpg.asset.json";
import repairCostAlt from "@/assets/blog/howmuchdoesroofrepair.jpg.asset.json";
import roofLifespan from "@/assets/blog/howlongdoes.jpg.asset.json";
import tileVsShingle from "@/assets/blog/whichisbetter.jpg.asset.json";
import tileSolar from "@/assets/blog/canyouinstall.jpg.asset.json";
import elCajonRepair from "@/assets/blog/roofrepairinelcajon.jpg.asset.json";
import project1 from "@/assets/blog/blog-1.jpg.asset.json";
import project2 from "@/assets/blog/blog-2.jpg.asset.json";
import project3 from "@/assets/blog/blog-3.jpg.asset.json";
import project4 from "@/assets/blog/blog-4.jpg.asset.json";
import project5 from "@/assets/blog/blog-5.png.asset.json";
import project6 from "@/assets/blog/blog-6.png.asset.json";
import roofRepair from "@/assets/mckay/roof-repair.webp.asset.json";
import roofReplacement from "@/assets/mckay/roof-replacement.webp.asset.json";
import roofingService from "@/assets/mckay/roofing-service.webp.asset.json";
import solarInstall from "@/assets/mckay/solar-install.webp.asset.json";
import solarEnergy from "@/assets/mckay/solar-energy.webp.asset.json";
import team1981 from "@/assets/mckay/team-1981.webp.asset.json";

import { group1Posts } from "./blogPosts.group1";
import { group2Posts } from "./blogPosts.group2";
import { group3Posts } from "./blogPosts.group3";
import { group4Posts } from "./blogPosts.group4";
import { archivePosts1 } from "./blogPosts.archive1";
import { archivePosts2 } from "./blogPosts.archive2";
import { archivePosts3 } from "./blogPosts.archive3";
import { archivePosts4 } from "./blogPosts.archive4";

export type Block =
  | { type: "p"; text: string }
  | { type: "h3"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "ol"; items: string[] }
  | { type: "callout"; title: string; text: string }
  | { type: "quote"; text: string; cite?: string };

export type Section = {
  id: string;
  h2: string;
  blocks: Block[];
  image?: { src: string; alt: string; caption: string };
};

export type BlogPost = {
  slug: string;
  title: string;
  h1: string;
  metaDescription: string;
  keywords: string;
  category: string;
  datePublished: string;
  dateModified: string;
  readMinutes: number;
  hero: { src: string; alt: string; caption: string };
  intro: string[];
  tldr: string[];
  sections: Section[];
  faqs: { q: string; a: string }[];
  cta: { title: string; text: string };
  ctaType?: "roofing" | "solar";
  /** Archive posts live on /blog/archive instead of the main /blog listing. */
  archive?: boolean;
};

export const AUTHOR = {
  name: "The McKay Roofing & Solar Crew",
  role: "Licensed roofers and solar installers (CSLB 1130793), on Southern California roofs since 1981",
  bioPath: "/about",
};

const basePosts: BlogPost[] = [
  {
    slug: "roof-repair-vs-replacement-san-diego",
    title: "Roof Repair vs Replacement: How to Choose | McKay",
    h1: "Roof repair vs replacement: which one does my Southern California roof actually need?",
    metaDescription:
      "Roof repair vs replacement, decided the way a roofer decides it: age, leak pattern, decking condition and cost per remaining year. Southern California specifics inside.",
    keywords:
      "roof repair vs replacement, when to replace a roof, Southern California roof replacement, roof repair cost, roof decking damage",
    category: "Roofing Insights",
    datePublished: "2026-03-17",
    dateModified: "2026-09-09",
    readMinutes: 7,
    hero: {
      src: heroRepairVsReplace.url,
      alt: "Side by side view of a patched roof section next to a fully replaced roof on a Southern California home",
      caption: "The same house can need a $600 repair or a full tear-off. The difference is usually what is under the shingles.",
    },
    intro: [
      "Most homeowners asking about roof repair vs replacement have already been told two different things by two different contractors. That is not because roofing is mysterious. It is because one of them priced the leak and the other priced the roof.",
      "A repair is the right call when the roof is structurally sound and the failure is local: a cracked boot, lifted flashing, a slipped tile, one bad valley. Replacement is the right call when the underlayment has aged out, the decking is soft, or you are on your third patch in two years.",
      "This post walks the same checks our crews run before quoting: age against material, the pattern of the leak, decking condition, code and permit reality in Southern California, and cost per remaining year of roof life.",
    ],
    tldr: [
      "Repair if the roof is under roughly 70% of its expected life, the leak is in one location, and the decking is firm.",
      "Replace if the underlayment is brittle, there are leaks in multiple planes, or you have paid for two or more patches in 24 months.",
      "Compare cost per remaining year, not sticker price: a $1,400 repair buying 3 years is worse value than a $16,000 roof buying 30.",
      "Tile roofs are the exception. The tile often outlives two underlayments, so a lift-and-relay beats a full replacement.",
    ],
    sections: [
      {
        id: "how-old-is-the-roof",
        h2: "How old is my roof, and does that alone decide it?",
        blocks: [
          {
            type: "p",
            text: "Age is the first filter because it tells you what the rest of the roof is doing, not just the part that is leaking. Materials in Southern California sun age on a fairly predictable curve.",
          },
          {
            type: "ul",
            items: [
              "**Three-tab asphalt shingle:** 15 to 20 years here, less on a west or south-facing slope.",
              "**Architectural / laminate shingle:** 22 to 30 years.",
              "**Concrete or clay tile:** 50+ years for the tile, but 20 to 30 for the underlayment beneath it.",
              "**Torch-down or modified bitumen:** 15 to 25 years depending on cap sheet and ponding.",
              "**TPO or PVC single-ply:** 20 to 30 years at 60 mil.",
              "**Standing seam metal:** 40 to 60 years.",
            ],
          },
          {
            type: "p",
            text: "The practical rule: if the roof has used less than about 70% of that range, repair is usually defensible. Past 70%, a repair is a bridge, and it should be priced and understood as one.",
          },
        ],
        image: {
          src: roofLifespan.url,
          alt: "Aging asphalt shingle roof with granule loss on a sun-exposed Southern California slope",
          caption: "Granule loss and shiny, exposed asphalt mean the shingle is near the end of its service life, not just dirty.",
        },
      },
      {
        id: "one-leak-or-many",
        h2: "Is this one leak, or is the whole roof telling me something?",
        blocks: [
          {
            type: "p",
            text: "Repairs succeed when the failure has a single, findable cause. They fail when the leak is a symptom of general wear.",
          },
          { type: "h3", text: "Signs it is a local failure (repair)" },
          {
            type: "ul",
            items: [
              "Water shows up in one ceiling spot, always after driving rain from the same direction.",
              "The stain sits directly below a penetration: vent, skylight, swamp cooler, solar stanchion, chimney.",
              "You can point to physical damage - a cracked plumbing boot, a lifted piece of step flashing, three slipped tiles.",
            ],
          },
          { type: "h3", text: "Signs the roof is done (replacement)" },
          {
            type: "ul",
            items: [
              "Stains in two or more rooms on different roof planes.",
              "Underlayment tears when touched or comes away as dry, brittle flakes.",
              "Nail heads backing out across whole slopes, or shingles curling at the field rather than the edges.",
              "Ponding on a flat section that never fully dries, with blisters in the cap sheet.",
            ],
          },
          {
            type: "callout",
            title: "The two-patch rule",
            text: "If you have paid for two roof repairs in the last 24 months and still have a wet ceiling, stop buying patches. The third one is a deposit on a roof you are going to replace anyway.",
          },
        ],
        image: {
          src: leakCauses.url,
          alt: "Roof valley and flashing detail where Southern California roof leaks most often begin",
          caption: "Valleys, flashing and penetrations cause most of the leaks we chase. Open field leaks usually mean age.",
        },
      },
      {
        id: "decking-condition",
        h2: "What does the decking tell us that the surface cannot?",
        blocks: [
          {
            type: "p",
            text: "The single most expensive surprise in roofing is soft decking, and it is invisible from the ground. A roof can look tidy while the plywood under a valley has been damp for four winters.",
          },
          {
            type: "p",
            text: "We check it two ways. From above, we walk the plane and feel for sponginess and dips between rafters. From below, in the attic, we look for daylight, dark water tracking down rafters, and rusted nail shanks.",
          },
          {
            type: "ol",
            items: [
              "Firm decking, isolated damage: repair, replace one or two sheets if needed.",
              "Soft decking under a single penetration: repair, but expect $150 to $400 per sheet of replaced plywood.",
              "Soft decking in multiple areas: replace. Patching over compromised sheathing is how a leak becomes a framing repair.",
            ],
          },
          {
            type: "quote",
            text: "Water does not travel in a straight line. It follows the underlayment until it finds a nail hole, which is why the wet spot on your ceiling is rarely under the actual failure.",
            cite: "What our crews explain on almost every leak call",
          },
        ],
        image: {
          src: roofRepair.url,
          alt: "McKay roofer inspecting decking and underlayment during a Southern California roof repair",
          caption: "Pulling back a section is the only honest way to price a repair. Anything else is a guess.",
        },
      },
      {
        id: "southern-california-factors",
        h2: "What is different about this decision in Southern California?",
        blocks: [
          {
            type: "p",
            text: "Our climate wears roofs unevenly, and that changes the math compared with advice written for wetter states.",
          },
          {
            type: "ul",
            items: [
              "**UV, not rain, is the main killer.** Shingles and cap sheets dry out and lose flexibility long before they look worn out.",
              "**Rain arrives in bursts.** Long dry spells then heavy atmospheric-river rain means drainage and flashing take the abuse, not the field.",
              "**Coastal salt air** attacks fasteners and metal flashing much faster within a few miles of the beach - Ocean Beach roofs and Riverside roofs do not age alike.",
              "**Wildfire zones** push tile, metal and Class A assemblies, which shifts replacement toward materials that also last longer.",
              "**Title 24 and cool-roof rules** apply to most re-roofs, which affects material choice and permit scope on replacement, not on a small repair.",
            ],
          },
          {
            type: "p",
            text: "If you want the local detail for your city, our [Southern California service areas](/areas-served) pages break down conditions block by block, and our [roof repair service page](/services/roof-repair) covers what a repair visit includes.",
          },
        ],
        image: {
          src: project3.url,
          alt: "Coastal flat roof replacement in Ocean Beach with new torch-down system and improved drainage",
          caption: "A coastal flat roof a few blocks from the sand: salt air and standing water shorten every timeline.",
        },
      },
      {
        id: "cost-comparison",
        h2: "How do the real numbers compare in 2026?",
        blocks: [
          {
            type: "p",
            text: "Here are the ranges we quote in Southern California. Your roof will land inside them based on pitch, access, layers to remove and material choice.",
          },
          {
            type: "ul",
            items: [
              "**Minor repair** (boot, small flashing fix, a few shingles or tiles): $450 to $1,200.",
              "**Moderate repair** (valley rebuild, skylight reflash, decking sheet or two): $1,200 to $3,500.",
              "**Section replacement** (one slope or one flat area): $4,000 to $9,000.",
              "**Full shingle replacement**, average home: $13,000 to $24,000.",
              "**Tile lift-and-relay** with new underlayment: $11,000 to $20,000.",
              "**Flat roof, new 60 mil TPO or torch-down:** $12 to $20 per square foot installed.",
            ],
          },
          {
            type: "callout",
            title: "Do the cost-per-year math",
            text: "A $1,400 repair on a 24-year-old shingle roof buys maybe 3 years: about $470 a year. A $18,000 replacement rated for 30 years costs about $600 a year and resets everything above the decking, including the underlayment and the flashing. That is the comparison worth making.",
          },
          {
            type: "p",
            text: "We break costs down further in our post on [real 2026 roof repair prices](/blog/roof-repair-cost), and full-system pricing lives on the [roof replacement page](/services/roof-replacement).",
          },
        ],
        image: {
          src: repairCost.url,
          alt: "Roof repair cost estimate being reviewed on a Southern California residential roofing project",
          caption: "Written scope beats a verbal number. Every line should name a material and a quantity.",
        },
      },
      {
        id: "insurance-and-timing",
        h2: "Will insurance pay, and how long can I wait?",
        blocks: [
          {
            type: "p",
            text: "Homeowners policies in California generally cover sudden, accidental damage - wind lifting a section, a branch punching through - and generally exclude wear, age and deferred maintenance. That distinction decides most claims.",
          },
          {
            type: "ol",
            items: [
              "Photograph everything before anyone climbs up, including the interior stain and the date.",
              "Stop the water with a tarp or temporary patch. Insurers expect mitigation and will ask.",
              "Get a written inspection report that separates storm damage from age-related wear.",
              "File before your policy's reporting window closes, usually within a year of the event.",
            ],
          },
          {
            type: "p",
            text: "On timing: a small leak in June can wait for a scheduled repair. The same leak in late November should not. Once decking stays wet through a rainy stretch, you are adding sheathing and sometimes drywall and insulation to the bill. The California Department of Insurance publishes plain-language guidance on documenting property claims at [insurance.ca.gov](https://www.insurance.ca.gov/), and the [NRCA](https://www.nrca.net/) maintains homeowner resources on roof system condition. We handle documentation on our [roof insurance claims](/services/roof-insurance-claims) service.",
          },
        ],
        image: {
          src: repairBasics.url,
          alt: "Homeowner and roofer reviewing storm damage documentation for a Southern California roof insurance claim",
          caption: "Dated photos and a written condition report are what turn a claim into a paid claim.",
        },
      },
      {
        id: "how-to-decide",
        h2: "What is the fastest way to decide with confidence?",
        blocks: [
          {
            type: "p",
            text: "Run these five questions in order. Two or more \"yes\" answers in the second column means you are replacing, not repairing.",
          },
          {
            type: "ol",
            items: [
              "Is the roof past 70% of its expected life for its material?",
              "Are there leaks on more than one roof plane?",
              "Is the underlayment brittle, torn, or already exposed?",
              "Have you paid for two or more repairs in the last two years?",
              "Did the inspection find soft decking in more than one area?",
            ],
          },
          {
            type: "p",
            text: "If you answered no to all five, get the repair done properly and move on. If you are somewhere in the middle, ask for both numbers in writing on the same visit - a scoped repair and a full replacement - so you are comparing options, not sales pitches.",
          },
        ],
        image: {
          src: roofReplacement.url,
          alt: "Completed asphalt shingle roof replacement on a Southern California home by McKay Roofing and Solar",
          caption: "A finished replacement resets underlayment, flashing and fasteners, not just the visible surface.",
        },
      },
    ],
    faqs: [
      {
        q: "Can you repair just one section of a roof?",
        a: "Yes. Section repairs are common on hip roofs and on homes with a sloped roof over living space and a flat roof over an addition. The caution is color and profile matching on shingles, and settling differences where old meets new.",
      },
      {
        q: "How long does a roof repair take compared with a replacement?",
        a: "Most repairs are a half day to a full day. A typical residential replacement runs two to four days including tear-off, decking repairs, underlayment, and final cleanup and inspection.",
      },
      {
        q: "Does a new roof increase home value in Southern California?",
        a: "It mostly protects value rather than adding it. Buyers and appraisers treat an aged roof as a deferred cost and negotiate against it, and a documented new roof with a manufacturer warranty removes that objection.",
      },
      {
        q: "Should I repair the roof before installing solar?",
        a: "If the roof has less than about 10 years left, address it first. Removing and reinstalling panels later typically costs $2,000 to $5,000, which is money spent twice.",
      },
    ],
    cta: {
      title: "Not sure which one your roof needs?",
      text: "We will inspect it, show you photos of what we found, and quote both options in writing - repair and replacement - so you can decide with real numbers.",
    },
    ctaType: "roofing",
  },
  {
    slug: "roof-leak-repair-san-diego-causes",
    title: "Roof Leak Causes and Fast Fixes | McKay Roofing & Solar",
    metaDescription:
      "The seven roof leak causes we find most often in Southern California, how to trace water back to the real source, and what a correct repair looks like for each one.",
    h1: "What causes roof leaks in Southern California, and how do you fix them fast?",
    keywords:
      "roof leak causes, roof leak repair Southern California, flashing leak, roof valley leak, clogged roof drain, emergency roof repair",
    category: "Roofing Insights",
    datePublished: "2026-03-17",
    dateModified: "2026-09-09",
    readMinutes: 6,
    hero: {
      src: leakCauses.url,
      alt: "Roof flashing and valley detail where most Southern California roof leaks begin",
      caption: "Almost every leak we chase starts at a joint, a penetration or a drain, not in the open field of the roof.",
    },
    intro: [
      "Roof leaks in Southern California rarely start where the ceiling is wet. Water enters at a joint, runs along the underlayment or a rafter, and drips out at the first nail hole it finds - sometimes ten feet away.",
      "That is why the fastest fix starts with tracing, not patching. Over four decades of local roofs, the same seven causes account for the large majority of calls we take.",
      "Below is each cause, how to recognize it from inside and outside the house, and what a correct repair actually involves so you know whether a quote makes sense.",
    ],
    tldr: [
      "Flashing failures at walls, chimneys and skylights are the number one cause we find.",
      "Cracked plumbing vent boots are the cheapest and most common single-point leak: usually $250 to $600.",
      "Clogged drains and scuppers cause most flat-roof leaks here, and they are maintenance, not damage.",
      "A leak that appears only in wind-driven rain is almost always flashing or a wall detail, not the roof field.",
    ],
    sections: [
      {
        id: "flashing-failures",
        h2: "Why is flashing the most common roof leak cause?",
        blocks: [
          {
            type: "p",
            text: "Flashing is the metal that bridges the roof and anything interrupting it: a wall, a chimney, a skylight curb, a dormer. Every one of those is a seam, and seams move.",
          },
          {
            type: "p",
            text: "Southern California makes it worse. Daily temperature swings expand and contract the metal, sealant dries out under UV, and the fasteners loosen a little each season.",
          },
          { type: "h3", text: "How to spot it" },
          {
            type: "ul",
            items: [
              "Water only appears when rain is driven from one direction.",
              "The interior stain follows a wall line or sits beside a chimney or skylight.",
              "From the roof, you can see lifted metal edges, cracked mastic, or caulk used where step flashing belongs.",
            ],
          },
          {
            type: "callout",
            title: "Caulk is not a flashing repair",
            text: "If a previous repair looks like a thick bead of sealant smeared along a wall, that is a patch with a two-year life. A correct fix removes the courses, installs step flashing woven into the roofing, and adds counterflashing at the wall.",
          },
        ],
        image: {
          src: roofRepair.url,
          alt: "Roofer replacing step flashing at a wall junction during a leak repair in Southern California",
          caption: "Step flashing woven into each course is the fix. Sealant over the top is a delay.",
        },
      },
      {
        id: "plumbing-boots",
        h2: "What about cracked vent boots and pipe penetrations?",
        blocks: [
          {
            type: "p",
            text: "The rubber collar around a plumbing vent is the shortest-lived part of most roofs. In our sun it typically cracks at 8 to 12 years, well before the shingles are done.",
          },
          {
            type: "p",
            text: "The clue is location: a small, repeatable drip almost directly below a pipe, often into a bathroom or hallway ceiling. It usually shows up in the first real rain of the season.",
          },
          {
            type: "ol",
            items: [
              "Remove the shingles or tiles around the penetration.",
              "Install a new boot with a metal base flange, not a slip-on rubber sleeve over the old one.",
              "Re-lay the courses so the flange is under the upslope material and over the downslope material.",
            ],
          },
          {
            type: "p",
            text: "Cost is usually $250 to $600 per penetration. If a quote is far above that for a boot alone, ask what else is in the scope.",
          },
        ],
        image: {
          src: elCajonRepair.url,
          alt: "Close view of a roof pipe penetration and new boot flashing on an inland Southern California home",
          caption: "New boots get a metal flange tucked under the upslope course. Sleeves over old rubber fail again.",
        },
      },
      {
        id: "valleys-and-drainage",
        h2: "Why do valleys and drains cause so many leaks here?",
        blocks: [
          {
            type: "p",
            text: "Our rain arrives in short, heavy bursts. Volume, not duration, is the stress test - and every gallon on the roof funnels through valleys, gutters, scuppers and drains.",
          },
          { type: "h3", text: "Sloped roofs: valleys" },
          {
            type: "ul",
            items: [
              "Debris packs into the valley, holds moisture against the material, and eventually rots the underlayment.",
              "Closed-cut valleys with worn metal underneath leak once granules wear through the shingle edge.",
              "Tiles pushed out of alignment in a valley leave a direct channel to the underlayment.",
            ],
          },
          { type: "h3", text: "Flat roofs: drains and scuppers" },
          {
            type: "ul",
            items: [
              "A clogged drain turns a 2 inch ponding area into standing water for weeks after a storm.",
              "Standing water finds every seam and blister and works under the cap sheet.",
              "Scuppers with failed sheet metal leak into the wall rather than the ceiling, which hides the damage longer.",
            ],
          },
          {
            type: "quote",
            text: "Clear your drains twice a year and you will avoid a large share of the flat-roof leaks we get called out for.",
            cite: "McKay commercial roofing crew",
          },
        ],
        image: {
          src: project2.url,
          alt: "Flat torch-down roof with new drainage detail installed on a Southern California commercial building",
          caption: "Drainage fixes are cheap compared with what standing water does to a cap sheet over one winter.",
        },
      },
      {
        id: "aging-underlayment",
        h2: "How does aging underlayment turn into an interior leak?",
        blocks: [
          {
            type: "p",
            text: "Shingles and tiles shed most of the water. The underlayment is what stops what gets past them, and it is the layer that quietly ages out first on tile roofs.",
          },
          {
            type: "p",
            text: "On a 30 year old tile roof with original felt, the tiles can look excellent while the felt beneath is brittle and cracked. That is why tile roofs often need a lift-and-relay rather than a full replacement - see our [tile roofing service page](/services/tile-roofing) for what that involves.",
          },
          {
            type: "callout",
            title: "Telltale sign",
            text: "Leaks that appear in several locations after long, steady rain - but not during short showers - usually mean the underlayment is saturating rather than a single joint failing.",
          },
        ],
        image: {
          src: tileVsShingle.url,
          alt: "Tile roof section lifted to show underlayment condition on a Southern California home",
          caption: "The tile is fine. The felt beneath it is what fails first, usually between years 20 and 30.",
        },
      },
      {
        id: "wind-and-storm-damage",
        h2: "What does wind and storm damage look like on a local roof?",
        blocks: [
          {
            type: "p",
            text: "Santa Ana events do most of the wind damage we see, and it concentrates at edges: ridges, rakes, eaves and the first few courses.",
          },
          {
            type: "ul",
            items: [
              "Missing or creased shingles along a ridge or rake, often with exposed nail heads.",
              "Lifted metal edge trim or drip edge on flat roofs.",
              "Slipped or broken tiles, usually field tiles that were already loose.",
              "Debris impact points from branches, with a bruise but no clear hole.",
            ],
          },
          {
            type: "p",
            text: "This category matters for insurance because it is sudden and accidental. Photograph it dated, tarp it, and get a written report before repairs - our [emergency roof repair](/services/emergency-roof-repair) crews document as they work. The National Weather Service event archives at [weather.gov](https://www.weather.gov/) are useful for tying damage to a specific storm date.",
          },
        ],
        image: {
          src: project4.url,
          alt: "Storm damaged roof edge being repaired during a Southern California re-roof project",
          caption: "Wind damage shows up at edges and ridges first, which is also where insurers look.",
        },
      },
      {
        id: "solar-and-penetrations",
        h2: "Can solar panels cause a roof leak?",
        blocks: [
          {
            type: "p",
            text: "Properly installed solar does not cause leaks. Poorly flashed mounting does, and we get called for it regularly.",
          },
          {
            type: "ul",
            items: [
              "Stanchions lagged into decking with sealant only, no flashed base.",
              "Tile roofs with mounts set on broken tiles that were never replaced.",
              "Conduit penetrations added after the fact with a bead of caulk.",
            ],
          },
          {
            type: "p",
            text: "The right sequence is roof first, panels second. If you are considering panels, read our guide to [installing solar on a tile roof](/blog/solar-on-tile-roof), and if panels need to come off for a repair we handle [panel removal and reinstallation](/services/solar-panel-removal-reinstallation).",
          },
        ],
        image: {
          src: solarInstall.url,
          alt: "Solar panel mounting hardware correctly flashed into a Southern California roof",
          caption: "Every mount is a hole in your roof. Flashed bases, not sealant, are what keep them dry.",
        },
      },
      {
        id: "what-to-do-now",
        h2: "What should I do right now if my roof is leaking?",
        blocks: [
          {
            type: "ol",
            items: [
              "Contain the water inside: bucket, plastic sheeting, and move anything valuable.",
              "Relieve pressure on a bulging ceiling with a small pinhole so it drains in one place instead of collapsing.",
              "Photograph the stain, the room and the date. Repeat outside if it is safe from the ground.",
              "Tarp from the ridge down, not just over the wet area, so water is diverted rather than trapped.",
              "Book an inspection. Ask specifically for photos of the source, not just a total price.",
            ],
          },
          {
            type: "p",
            text: "Do not climb a wet roof. Tile is slick, older shingles tear under foot, and a fall costs far more than a repair.",
          },
        ],
        image: {
          src: roofingService.url,
          alt: "McKay Roofing crew arriving to diagnose a roof leak at a Southern California home",
          caption: "A good leak call ends with photos of the actual source, not just an invoice.",
        },
      },
    ],
    faqs: [
      {
        q: "Why does my roof only leak sometimes?",
        a: "Intermittent leaks almost always mean a directional flashing or wall detail. Water gets in only when rain is driven at the right angle or when volume is high enough to back up a valley or drain.",
      },
      {
        q: "How much does a roof leak repair cost in Southern California?",
        a: "Most single-source leak repairs run $450 to $1,200. Valley rebuilds, skylight reflashing and decking replacement push it to $1,200 to $3,500.",
      },
      {
        q: "Can a leak be fixed in the rain?",
        a: "Temporary mitigation yes, a permanent repair usually no. Adhesives and torch-applied materials need dry, clean surfaces, so we tarp first and return to do the work properly.",
      },
      {
        q: "Will a leak always show on the ceiling?",
        a: "No. Water often runs into walls, insulation or a soffit and shows up as a musty smell, peeling paint or a bubbling wall long before a ceiling stain appears.",
      },
    ],
    cta: {
      title: "Have an active leak?",
      text: "We will find the source, tarp it the same visit when needed, and send you photos with a written repair scope before any work starts.",
    },
    ctaType: "roofing",
  },
  {
    slug: "roof-repair-cost-el-cajon-ca-2026",
    title: "Roof Repair Cost in 2026: Real Price Ranges | McKay",
    h1: "How much does roof repair cost in Southern California in 2026?",
    metaDescription:
      "Real 2026 roof repair cost ranges for Southern California homes, what drives the price up, which repairs are worth it, and when patching stops making financial sense.",
    keywords:
      "roof repair cost, roof repair price 2026, Southern California roof repair, roof repair estimate, cost to fix roof leak",
    category: "Roofing Insights",
    datePublished: "2026-03-18",
    dateModified: "2026-09-09",
    readMinutes: 6,
    hero: {
      src: repairCost.url,
      alt: "Roof repair cost estimate reviewed on site for a Southern California residential roof",
      caption: "Prices below are the ranges we actually quote in Southern California in 2026, not national averages.",
    },
    intro: [
      "Most roof repairs in Southern California land between $450 and $3,500 in 2026. The spread is wide because two repairs that sound identical can differ by a factor of five once access, pitch and decking come into it.",
      "Here is what each repair type costs, what pushes a bill up, and the point where paying again for patches stops making sense.",
      "Every number below is a range we quote locally. Treat any estimate given over the phone without an inspection as marketing, not pricing.",
    ],
    tldr: [
      "Minor repairs: $450 to $1,200. Moderate: $1,200 to $3,500. Section replacement: $4,000 to $9,000.",
      "Steep pitch, tile, second-story access and multiple existing layers each add real cost.",
      "Decking replacement runs about $150 to $400 per sheet and is the most common surprise.",
      "Once repairs exceed roughly 30% of replacement cost on an aged roof, replace instead.",
    ],
    sections: [
      {
        id: "price-by-repair-type",
        h2: "What does each type of roof repair cost?",
        blocks: [
          {
            type: "ul",
            items: [
              "**Plumbing vent boot replacement:** $250 to $600 per penetration.",
              "**Replace a handful of shingles:** $400 to $900.",
              "**Reset or replace slipped tiles:** $450 to $1,100 depending on tile availability.",
              "**Step or wall flashing repair:** $700 to $2,000.",
              "**Skylight reflash (not replace):** $850 to $2,200.",
              "**Valley rebuild:** $1,200 to $3,000.",
              "**Chimney flashing and counterflashing:** $1,200 to $2,800.",
              "**Flat roof patch and seam repair:** $600 to $2,500 depending on area.",
              "**Decking / sheathing replacement:** $150 to $400 per 4x8 sheet.",
              "**Section replacement, one slope or flat area:** $4,000 to $9,000.",
            ],
          },
          {
            type: "p",
            text: "Emergency and after-hours calls typically add $200 to $500, and tarping as mitigation is usually $300 to $800 depending on area covered.",
          },
        ],
        image: {
          src: repairCostAlt.url,
          alt: "Itemized roof repair scope being reviewed with a Southern California homeowner",
          caption: "Ask for line items. A single lump sum hides whether decking and flashing are included.",
        },
      },
      {
        id: "what-drives-price",
        h2: "What actually drives my roof repair price up?",
        blocks: [
          {
            type: "p",
            text: "Six factors explain nearly all the variation between quotes on the same roof.",
          },
          {
            type: "ol",
            items: [
              "**Pitch.** Anything over 6:12 needs fall protection and slows the crew, adding 15% to 30% in labor.",
              "**Material.** Tile requires careful walking and matching stock; concrete tile from the 1980s can be hard to source.",
              "**Access.** Second story, tight side yards, no truck access, or panels in the way all add hours.",
              "**Existing layers.** Two or three layers of old roofing means removal cost before any repair begins.",
              "**Hidden damage.** Soft decking, rotted fascia or a wet wall found once the area is opened.",
              "**Permits.** Small repairs usually do not need one; anything structural or over a set square footage does.",
            ],
          },
          {
            type: "callout",
            title: "Ask for a contingency line",
            text: "A good estimate names a decking price per sheet up front. That way if a crew finds two bad sheets, you already know the number instead of getting a change order you cannot check.",
          },
        ],
        image: {
          src: project5.url,
          alt: "Two story Southern California home with mixed sloped and flat roofing sections being repaired",
          caption: "Mixed roof types on one house mean two systems, two material sets and two labor rates.",
        },
      },
      {
        id: "repair-or-replace-math",
        h2: "At what point should I stop repairing and replace?",
        blocks: [
          {
            type: "p",
            text: "Use two thresholds together. Neither works alone.",
          },
          {
            type: "ul",
            items: [
              "**The 30% rule:** if a repair costs more than about 30% of replacement, and the roof is past 70% of its expected life, replace.",
              "**Cost per remaining year:** divide each option by the years it realistically buys. A $2,800 repair on a roof with 4 years left is $700 a year. A $17,000 roof rated 30 years is about $570 a year.",
            ],
          },
          {
            type: "p",
            text: "There is a third, less obvious factor: repeat interior damage. Drywall, paint, insulation and flooring costs add up quietly, and they never appear on a roofing invoice. Our [repair vs replacement guide](/blog/roof-repair-vs-replacement) walks the full decision.",
          },
        ],
        image: {
          src: heroRepairVsReplace.url,
          alt: "Comparison of a repaired roof area and a fully replaced roof on a Southern California property",
          caption: "Compare cost per remaining year, not the sticker price of a single visit.",
        },
      },
      {
        id: "insurance-coverage",
        h2: "Will insurance cover any of my roof repair cost?",
        blocks: [
          {
            type: "p",
            text: "Sudden and accidental damage is generally covered. Age, wear and neglected maintenance are generally not. That is the whole test, and documentation decides which category a claim lands in.",
          },
          {
            type: "ol",
            items: [
              "Date-stamped photos before any work, inside and out.",
              "Mitigation first: tarp, contain water, keep receipts.",
              "A written inspection report separating storm damage from wear.",
              "File within your policy's reporting window, typically one year.",
            ],
          },
          {
            type: "p",
            text: "Deductibles in California commonly run $1,000 to $2,500, which means many minor repairs are not worth a claim at all. The California Department of Insurance publishes claim guidance at [insurance.ca.gov](https://www.insurance.ca.gov/). We handle documentation through our [roof insurance claims](/services/roof-insurance-claims) service.",
          },
        ],
        image: {
          src: repairBasics.url,
          alt: "Roofer documenting damage for an insurance claim on a Southern California roof repair",
          caption: "The report language matters. \"Wind lifted the ridge course\" reads differently than \"worn shingles.\"",
        },
      },
      {
        id: "avoiding-overpaying",
        h2: "How do I avoid overpaying for a roof repair?",
        blocks: [
          {
            type: "ul",
            items: [
              "Get the repair scoped in writing with materials named - underlayment type, flashing gauge, fastener spec.",
              "Verify the contractor's license number on the [CSLB license lookup](https://www.cslb.ca.gov/). Ours is CSLB 1130793.",
              "Ask for photos of the source before and after. A crew that cannot show you the failure did not find it.",
              "Refuse large deposits. California limits residential down payments to $1,000 or 10% of the contract, whichever is less.",
              "Do not accept caulk-only repairs at flashing, walls or valleys.",
            ],
          },
          {
            type: "quote",
            text: "The cheapest repair is the one you only pay for once. Two $700 patches on the same valley cost more than doing it right the first time.",
            cite: "McKay Roofing & Solar",
          },
        ],
        image: {
          src: team1981.url,
          alt: "McKay Roofing and Solar crew who have worked Southern California roofs since 1981",
          caption: "Ask who is actually on your roof. Licensed crews, not day-labor subs, is a fair question.",
        },
      },
      {
        id: "maintenance-savings",
        h2: "What maintenance keeps repair costs down?",
        blocks: [
          {
            type: "p",
            text: "The cheapest roofing money you will ever spend is on drainage and inspection. Both are boring and both work.",
          },
          {
            type: "ol",
            items: [
              "Clear gutters, valleys, scuppers and drains twice a year - before and after the rainy season.",
              "Trim branches back at least 6 feet from the roof to stop abrasion and debris.",
              "Get an inspection every 2 to 3 years, annually past year 15.",
              "Replace vent boots proactively at year 10 while the crew is already on the roof.",
              "Fix small flashing issues in dry months when scheduling is cheaper and easier.",
            ],
          },
          {
            type: "p",
            text: "Our [roof maintenance program](/services/roof-maintenance) covers the same checklist on a schedule, and full replacement pricing is on the [roof replacement page](/services/roof-replacement).",
          },
        ],
        image: {
          src: project6.url,
          alt: "Well maintained synthetic tile roof on a Southern California mountain property",
          caption: "Maintained roofs reach the top of their expected lifespan. Neglected ones rarely do.",
        },
      },
    ],
    faqs: [
      {
        q: "Is it cheaper to repair or replace a roof?",
        a: "A repair is always cheaper today. Over the remaining life of an aged roof it often is not, because repeat patches plus interior damage can exceed the cost of a replacement that resets the whole system.",
      },
      {
        q: "Do roofers charge for estimates?",
        a: "We do not charge for a repair or replacement estimate. Detailed forensic leak investigations that require opening up sections are sometimes billed and credited toward the work.",
      },
      {
        q: "How long should a roof repair last?",
        a: "A properly executed flashing, boot or valley repair should last the remaining life of the roof material around it. If a repair fails within two years, the diagnosis was wrong, not the material.",
      },
      {
        q: "Can I finance a roof repair?",
        a: "Repairs are usually paid directly, while replacements are commonly financed. Ask about terms in writing and compare the total cost of credit, not just the monthly payment.",
      },
    ],
    cta: {
      title: "Want a real number for your roof?",
      text: "Free inspection, photos of what we find, and a written itemized estimate with a decking contingency price so there are no surprises.",
    },
    ctaType: "roofing",
  },
  {
    slug: "solar-on-tile-roof-el-cajon-ca",
    title: "Solar on a Tile Roof: What to Know First | McKay",
    h1: "Can you install solar on a tile roof without cracking it?",
    metaDescription:
      "Installing solar on a tile roof works when the mounting and sequence are right. How tile hooks and flashed mounts work, what cracks tile, and what it costs in 2026.",
    keywords:
      "solar on tile roof, tile roof solar installation, solar panel mounting tile, tile roof underlayment solar, solar roof inspection",
    category: "Solar Insights",
    datePublished: "2026-03-18",
    dateModified: "2026-09-09",
    readMinutes: 6,
    hero: {
      src: tileSolar.url,
      alt: "Solar panels installed on a concrete tile roof on a Southern California home",
      caption: "Tile and solar work together. The order of operations is what decides whether you get leaks later.",
    },
    intro: [
      "Yes, you can install solar on a tile roof - concrete, clay or synthetic. Tile is the most common roof type on Southern California homes with panels, and it performs well when mounted correctly.",
      "The risks are real but specific: cracked tiles during installation, unflashed penetrations, and panels installed over underlayment that only had a few years left.",
      "This post covers how tile mounting actually works, what causes cracks, why the roof inspection has to come first, and 2026 cost ranges including panel removal and reinstall.",
    ],
    tldr: [
      "Tile solar uses tile hooks or tile-replacement flashed mounts - never lag bolts through the tile face.",
      "Get a roof inspection first. If the underlayment has under 10 years left, do the roof before the panels.",
      "Expect 2% to 5% tile breakage during install; a good installer carries matching stock and replaces them.",
      "Removing and reinstalling panels later runs $2,000 to $5,000, which is why sequence matters more than price.",
    ],
    sections: [
      {
        id: "how-tile-mounting-works",
        h2: "How does solar mounting work on a tile roof?",
        blocks: [
          {
            type: "p",
            text: "The goal is to reach the rafter for structural support while keeping the water barrier intact. There are two accepted ways to do that on tile.",
          },
          { type: "h3", text: "Tile hooks" },
          {
            type: "p",
            text: "An L-shaped bracket is lagged into the rafter and routed out beneath the tile above it. The tile is notched or lifted so the hook exits without bearing weight on the tile itself. Fast, and good on S-profile clay.",
          },
          { type: "h3", text: "Tile replacement mounts" },
          {
            type: "p",
            text: "One tile is removed and replaced with a metal flashing pan that mimics the tile profile and carries the mount. This is our preference on flat-profile concrete tile because it puts a proper flashed metal surface at every penetration.",
          },
          {
            type: "callout",
            title: "What to never accept",
            text: "Lag bolts driven through the face of a tile and sealed with caulk. It cracks the tile, relies entirely on sealant, and is the single most common cause of the tile-solar leaks we get called to repair.",
          },
        ],
        image: {
          src: solarInstall.url,
          alt: "Flashed solar mount installed into a tile roof rafter on a Southern California home",
          caption: "Every mount reaches a rafter and gets a flashed base. That is the whole trick.",
        },
      },
      {
        id: "cracked-tiles",
        h2: "Will installers crack my tiles, and what happens if they do?",
        blocks: [
          {
            type: "p",
            text: "Some breakage is normal. Concrete tile becomes brittle with age, and crews have to walk the roof to work. Expect 2% to 5% of walked tiles to break on a 20+ year old roof.",
          },
          {
            type: "p",
            text: "The problem is not breakage - it is unreplaced breakage. A cracked tile left in place exposes underlayment to direct UV and water, and that is a leak in two to three winters.",
          },
          {
            type: "ul",
            items: [
              "Ask in writing who pays for replacement tiles and labor.",
              "Ask whether they source matching tile or use \"close enough\" stock.",
              "Ask for a post-install walkthrough with photos of every mount.",
              "For discontinued profiles, buy spare tiles now - matching gets harder every year.",
            ],
          },
        ],
        image: {
          src: tileVsShingle.url,
          alt: "Concrete tile roof surface showing tile condition before a solar installation",
          caption: "Older concrete tile is brittle. Breakage is expected, unreplaced breakage is negligence.",
        },
      },
      {
        id: "inspect-roof-first",
        h2: "Why does the roof inspection have to come before the panels?",
        blocks: [
          {
            type: "p",
            text: "Panels last 25 to 30 years. Tile underlayment lasts 20 to 30. If your underlayment is at year 24, you are about to bolt a 25 year system onto a 5 year roof.",
          },
          {
            type: "ol",
            items: [
              "Under 10 years of underlayment left: do a lift-and-relay first, then install panels.",
              "10 to 15 years left: judgment call. Weigh relay cost now against removal and reinstall later.",
              "Over 15 years left: install panels, no roof work needed.",
            ],
          },
          {
            type: "p",
            text: "A lift-and-relay reuses your existing tile and replaces the underlayment beneath it, typically $11,000 to $20,000 - see [tile roofing](/services/tile-roofing). Doing it before panels avoids paying separately to remove and reinstall the array.",
          },
          {
            type: "quote",
            text: "The most expensive solar mistake in Southern California is not the panels. It is installing them on a roof with five years left.",
            cite: "McKay Roofing & Solar",
          },
        ],
        image: {
          src: roofLifespan.url,
          alt: "Roof inspection on a tile home in Southern California ahead of a solar installation",
          caption: "One inspection before the array goes up prevents a five-figure sequencing mistake.",
        },
      },
      {
        id: "costs-2026",
        h2: "What does tile roof solar cost in 2026?",
        blocks: [
          {
            type: "ul",
            items: [
              "**Tile adder over composition shingle:** typically $0.10 to $0.25 per watt, or roughly $800 to $2,000 on a 8 kW system.",
              "**Typical 6 to 8 kW residential system, tile roof:** $18,000 to $28,000 before incentives.",
              "**Battery storage added:** $10,000 to $18,000 depending on capacity.",
              "**Panel removal and reinstall for later roof work:** $2,000 to $5,000.",
              "**Lift-and-relay underlayment replacement:** $11,000 to $20,000.",
            ],
          },
          {
            type: "p",
            text: "Federal incentive rules change, so confirm current terms at [energy.gov](https://www.energy.gov/) or with your tax professional rather than trusting a sales sheet. Utility rules also matter locally - our [utility companies guide](/utility-companies) covers SDG&E, SCE, LADWP and others.",
          },
        ],
        image: {
          src: solarEnergy.url,
          alt: "Completed rooftop solar array on a Southern California tile roof home",
          caption: "Tile adds cost per watt because mounting is slower, not because the panels differ.",
        },
      },
      {
        id: "warranty-and-permits",
        h2: "Does solar void my roof warranty, and what about permits?",
        blocks: [
          {
            type: "p",
            text: "Manufacturer material warranties usually survive a properly flashed installation. Workmanship warranties from your roofer are the ones at risk when a separate solar company drills the roof.",
          },
          {
            type: "ul",
            items: [
              "Ask your roofer in writing whether the planned mounting keeps the workmanship warranty intact.",
              "Using one contractor for both roof and solar removes the finger-pointing when a leak appears.",
              "Solar installs require a building and electrical permit plus utility interconnection approval in every Southern California jurisdiction we work in.",
              "Keep the as-built plan set and inspection sign-off. You will need them at resale.",
            ],
          },
          {
            type: "p",
            text: "We do both, which is why our [solar installation](/services/solar-installation) and roofing crews schedule together on the same project.",
          },
        ],
        image: {
          src: project1.url,
          alt: "New roof system and solar installation completed together on a Southern California building",
          caption: "Roof and array on the same project: one warranty conversation instead of two.",
        },
      },
      {
        id: "maintenance",
        h2: "How do I maintain a tile roof once panels are on it?",
        blocks: [
          {
            type: "p",
            text: "Access gets harder, so the maintenance plan should be set before the array goes up.",
          },
          {
            type: "ol",
            items: [
              "Keep gutters, valleys and any tile channels around the array clear - debris collects at panel edges.",
              "Have mounts and flashings checked every 2 to 3 years, not just the panels.",
              "Watch production data. A sudden drop can mean shading, soiling or an inverter issue.",
              "Never pressure wash panels or walk on tile yourself; both do more damage than the dirt.",
              "If a repair is needed under the array, use a crew that removes and reinstalls panels properly.",
            ],
          },
          {
            type: "p",
            text: "If your system is already underperforming, our [solar panel repair](/services/solar-panel-repair) team diagnoses production, wiring and mounting issues together.",
          },
        ],
        image: {
          src: roofingService.url,
          alt: "McKay crew servicing a roof with solar panels installed in Southern California",
          caption: "Maintenance under an array needs a crew comfortable with both roofing and electrical.",
        },
      },
    ],
    faqs: [
      {
        q: "Is solar more expensive on a tile roof?",
        a: "Yes, usually $800 to $2,000 more on a typical residential system. Mounting takes longer and requires flashed pans or tile hooks plus replacement tile stock.",
      },
      {
        q: "Can panels be installed on clay tile as well as concrete?",
        a: "Both work. Clay is more brittle and typically favors tile hooks with careful routing, while flat concrete tile suits tile-replacement flashed mounts.",
      },
      {
        q: "How long does a tile roof solar installation take?",
        a: "Two to four days of installation for a typical residential system, plus permit and utility interconnection time, which is usually the longer part of the schedule.",
      },
      {
        q: "Do I need to remove panels to replace the underlayment later?",
        a: "Yes. That is the $2,000 to $5,000 line item, and it is exactly the cost you avoid by doing a lift-and-relay before the array goes up.",
      },
    ],
    cta: {
      title: "Thinking about panels on a tile roof?",
      text: "We inspect the roof and the underlayment first, then quote the roof work and the solar together so the sequence is right the first time.",
    },
    ctaType: "solar",
  },
];

export const blogPosts: BlogPost[] = [
  ...basePosts,
  ...group1Posts,
  ...group2Posts,
  ...group3Posts,
  ...group4Posts,
  ...archivePosts1,
  ...archivePosts2,
  ...archivePosts3,
  ...archivePosts4,
];

/** Posts shown on the main /blog listing. */
export const featuredPosts: BlogPost[] = blogPosts.filter((p) => !p.archive);

/** The full recreated insights library, shown on /blog/archive. */
export const archivePosts: BlogPost[] = blogPosts.filter((p) => p.archive);

export function getPost(slug: string): BlogPost | undefined {
  return blogPosts.find((p) => p.slug === slug);
}
